/** Shared helpers so Overview Apex area charts keep a visible line for sparse series. */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** Period string → duration in ms. Matches backend `convertTimeRangePeriod2Timestamp`:
 *  m = minutes, h = hours, d = days, M = months. Also supports w / y for UI.
 */
export function parsePeriodMs(period = "7d") {
  // Case-sensitive unit (m ≠ M), same as backend helpers/time.js
  const m = String(period || "7d").trim().match(/^(\d+)\s*([mhdwMy])$/);
  if (!m) return 7 * DAY_MS;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return 7 * DAY_MS;
  switch (m[2]) {
    case "m":
      return n * MINUTE_MS;
    case "h":
      return n * HOUR_MS;
    case "d":
      return n * DAY_MS;
    case "w":
      return n * 7 * DAY_MS;
    case "M":
      return n * 30 * DAY_MS;
    case "y":
    case "Y":
      return n * 365 * DAY_MS;
    default:
      return 7 * DAY_MS;
  }
}

/**
 * Approximate ClickHouse getInterval() bucket size for overview stats series.
 * Backend multiplies period by 4 before picking the interval.
 */
export function overviewIntervalMs(period = "7d") {
  const diffSeconds = (parsePeriodMs(period) / 1000) * 4;
  if (diffSeconds > 30 * 24 * 3600) return 2 * HOUR_MS;
  if (diffSeconds > 7 * 24 * 3600) return HOUR_MS;
  if (diffSeconds > 4 * 24 * 3600) return 30 * MINUTE_MS;
  if (diffSeconds > 2 * 24 * 3600) return 20 * MINUTE_MS;
  if (diffSeconds > 1 * 24 * 3600) return 10 * MINUTE_MS;
  return 5 * MINUTE_MS;
}

export function overviewRangeMs(timeRange, nowMs = Date.now()) {
  if (timeRange?.from && timeRange?.to) {
    const from = new Date(timeRange.from).getTime();
    const to = new Date(timeRange.to).getTime();
    if (Number.isFinite(from) && Number.isFinite(to) && from < to) {
      return { fromMs: from, toMs: to };
    }
  }
  const periodMs = parsePeriodMs(timeRange?.period || "7d");
  return { fromMs: nowMs - periodMs, toMs: nowMs };
}

/** Normalize API key / ISO / epoch into chart x (ms). */
export function toChartTimestamp(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const asStr = String(value).trim();
  if (/^\d{10,13}$/.test(asStr)) {
    const n = Number(asStr);
    return asStr.length <= 10 ? n * 1000 : n;
  }
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Floor to bucket start in local time (matches CH toStartOfInterval with browser TZ). */
function alignBucketStartLocal(ms, intervalMs) {
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  const d = new Date(ms);
  if (step >= HOUR_MS) {
    d.setMinutes(0, 0, 0);
    const hours = Math.max(1, Math.round(step / HOUR_MS));
    if (hours > 1) {
      const h = d.getHours();
      d.setHours(Math.floor(h / hours) * hours, 0, 0, 0);
    }
    return d.getTime();
  }
  const stepMins = Math.max(1, Math.round(step / 60000));
  const totalMins = d.getHours() * 60 + d.getMinutes();
  const aligned = Math.floor(totalMins / stepMins) * stepMins;
  d.setHours(Math.floor(aligned / 60), aligned % 60, 0, 0);
  return d.getTime();
}

function nextBucketLocal(ms, intervalMs) {
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  if (step >= HOUR_MS) {
    const d = new Date(ms);
    d.setHours(d.getHours() + Math.max(1, Math.round(step / HOUR_MS)), 0, 0, 0);
    return d.getTime();
  }
  return ms + step;
}

/**
 * Fill quiet gaps with y=0 while preserving exact API timestamps/counts.
 * Never re-keys source points onto a UTC grid (that was dropping real spikes).
 *
 * @param {Array<[number|string, number]>} points
 */
export function densifyChartPoints(points, { fromMs, toMs, intervalMs }) {
  const step = Number.isFinite(intervalMs) && intervalMs > 0 ? intervalMs : HOUR_MS;
  const byT = new Map();
  for (const pt of points || []) {
    const t = toChartTimestamp(pt?.[0]);
    if (!Number.isFinite(t)) continue;
    // Keep the API timestamp as-is (CH bucket key), do not UTC-floor it away.
    byT.set(t, (byT.get(t) || 0) + (Number(pt[1]) || 0));
  }

  const raw = Array.from(byT.entries()).sort((a, b) => a[0] - b[0]);
  if (!raw.length) return [];

  // Expand window so spikes just outside "now" / period edges are never dropped.
  let rangeFrom = Number.isFinite(fromMs) ? fromMs : raw[0][0];
  let rangeTo = Number.isFinite(toMs) ? toMs : raw[raw.length - 1][0];
  rangeFrom = Math.min(rangeFrom, raw[0][0]);
  rangeTo = Math.max(rangeTo, raw[raw.length - 1][0] + step);

  if (!(rangeFrom < rangeTo)) return raw;

  const lookup = (bucketT) => {
    if (byT.has(bucketT)) return byT.get(bucketT);
    let best = null;
    let bestDist = Infinity;
    const half = step / 2;
    for (const [k, v] of byT) {
      const dist = Math.abs(k - bucketT);
      // Inclusive half-bucket so TZ/ISO offsets at the edge still match.
      if (dist <= half && dist < bestDist) {
        bestDist = dist;
        best = v;
      }
    }
    return best != null ? best : 0;
  };

  let t = alignBucketStartLocal(rangeFrom, step);
  if (t + step <= rangeFrom) t = nextBucketLocal(t, step);
  const last = alignBucketStartLocal(rangeTo - 1, step);
  const outMap = new Map();
  const maxPts = 5000;
  for (let guard = 0; t <= last && guard < maxPts; guard += 1) {
    outMap.set(t, lookup(t));
    t = nextBucketLocal(t, step);
  }

  // Always re-assert original API points so real counts cannot be lost to grid skew.
  for (const [ts, count] of raw) {
    outMap.set(ts, count);
  }

  return Array.from(outMap.entries()).sort((a, b) => a[0] - b[0]);
}

/**
 * Prepare Overview chart series:
 * - few API buckets: pad range endpoints with 0 so Apex draws a visible spike line
 * - many buckets: densify quiet gaps with y=0 (heatmap-style, preserves API counts)
 */
export function prepareOverviewSeries(points, { fromMs, toMs, intervalMs }) {
  const raw = [];
  const seen = new Map();
  for (const pt of points || []) {
    const t = toChartTimestamp(pt?.[0]);
    if (!Number.isFinite(t)) continue;
    const v = Number(pt[1]) || 0;
    seen.set(t, (seen.get(t) || 0) + v);
  }
  for (const e of seen.entries()) raw.push(e);
  raw.sort((a, b) => a[0] - b[0]);
  if (!raw.length) return [];

  if (raw.length <= 3) {
    let rangeFrom = Number.isFinite(fromMs) ? fromMs : raw[0][0];
    let rangeTo = Number.isFinite(toMs) ? toMs : raw[raw.length - 1][0];
    rangeFrom = Math.min(rangeFrom, raw[0][0]);
    rangeTo = Math.max(rangeTo, raw[raw.length - 1][0]);
    const out = [];
    if (rangeFrom < raw[0][0]) out.push([rangeFrom, 0]);
    out.push(...raw);
    if (rangeTo > raw[raw.length - 1][0]) out.push([rangeTo, 0]);
    return out;
  }

  return densifyChartPoints(raw, { fromMs, toMs, intervalMs });
}

/** Map ES/CH style buckets → [ms, count]. */
export function apiBucketsToPoints(items) {
  const out = [];
  for (const item of items || []) {
    const t = toChartTimestamp(item?.key_as_string ?? item?.key);
    if (!Number.isFinite(t)) continue;
    out.push([t, Number(item?.doc_count ?? item?.value ?? 0) || 0]);
  }
  return out;
}
